import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';

import type { AnswerCheck } from './gasLaws';
import { AnswerFeedback } from './GasLawsProblems';
import {
  QUALITY_GUIDELINES,
  WATER_SAMPLES,
  checkHardnessAnswer,
  checkSampleFlags,
  formatGuideline,
  formatPercent,
  hardnessClass,
  hardnessWorking,
  measuredParameters,
  needsHardnessCalculation,
  oxygenSaturationPercent,
  sampleById,
  sampleHardness,
  sampleValue,
  type QualityParameter,
  type SampleCheckResult,
  type WaterSample,
} from './waterChemistryQuality';
import { GuidelineMeter } from './WaterChemistryVisuals';

function readingRows(sample: WaterSample): { label: string; value: string }[] {
  const rows = [
    { label: 'pH', value: `${sample.ph}` },
    { label: 'Total dissolved solids', value: `${sample.tds} mg/L` },
  ];
  if (sample.hardness !== undefined) rows.push({ label: 'Total hardness', value: `${sample.hardness} mg/L as CaCO₃` });
  if (sample.calcium !== undefined) rows.push({ label: 'Calcium, Ca²⁺', value: `${sample.calcium} mg/L` });
  if (sample.magnesium !== undefined) rows.push({ label: 'Magnesium, Mg²⁺', value: `${sample.magnesium} mg/L` });
  if (sample.fluoride !== undefined) rows.push({ label: 'Fluoride, F⁻', value: `${sample.fluoride} mg/L` });
  if (sample.nitrate !== undefined) rows.push({ label: 'Nitrate, NO₃⁻', value: `${sample.nitrate} mg/L` });
  if (sample.lead !== undefined) rows.push({ label: 'Lead, Pb', value: `${sample.lead} mg/L` });
  rows.push({ label: 'Conductivity (25 °C)', value: `${sample.conductivity} µS/cm` });
  rows.push({ label: 'Dissolved oxygen', value: `${sample.dissolvedOxygen} mg/L at ${sample.temperatureC} °C` });
  return rows;
}

export function WaterChemistryQuality() {
  const [sampleId, setSampleId] = useState(WATER_SAMPLES[0].id);
  const [hardnessAnswer, setHardnessAnswer] = useState('');
  const [hardnessCheck, setHardnessCheck] = useState<AnswerCheck | null>(null);
  const [flags, setFlags] = useState<QualityParameter[]>([]);
  const [noneSelected, setNoneSelected] = useState(false);
  useScienceTutorSection('WaterChemistryQuality', { sampleId, hardnessAnswer, flags, noneSelected });
  const [result, setResult] = useState<SampleCheckResult | null>(null);
  const [completed, setCompleted] = useState<string[]>([]);
  const sample = sampleById(sampleId) ?? WATER_SAMPLES[0];
  const measured = measuredParameters(sample);
  const hardnessNeeded = needsHardnessCalculation(sample);
  const hardnessDone = !hardnessNeeded || (hardnessCheck !== null && hardnessCheck.status !== 'invalid');
  const saturation = oxygenSaturationPercent(sample.dissolvedOxygen, sample.temperatureC);

  const choose = (id: string) => {
    setSampleId(id);
    setHardnessAnswer('');
    setHardnessCheck(null);
    setFlags([]);
    setNoneSelected(false);
    setResult(null);
  };
  const toggle = (parameter: QualityParameter) => {
    setNoneSelected(false);
    setFlags((current) =>
      current.includes(parameter) ? current.filter((entry) => entry !== parameter) : [...current, parameter],
    );
    setResult(null);
  };
  const check = () => {
    const outcome = checkSampleFlags(sample, noneSelected ? [] : flags);
    setResult(outcome);
    if (outcome?.correct) setCompleted((current) => (current.includes(sample.id) ? current : [...current, sample.id]));
  };

  return (
    <section
      aria-label="Water quality samples"
      className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm font-medium leading-6 text-slate2">
          Read the results, calculate hardness when it is not given, then flag every value outside its guideline.
        </p>
        <span className="shrink-0 rounded-full bg-wash-mint px-3 py-1 text-xs font-extrabold text-ink-soft">
          {completed.length} of {WATER_SAMPLES.length} judged correctly
        </span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Choose a water sample">
        {WATER_SAMPLES.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === sample.id}
            onClick={() => choose(candidate.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${
              candidate.id === sample.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-white text-slate2'
            }`}
          >
            {completed.includes(candidate.id) ? '✓ ' : ''}
            {candidate.name}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold text-ink">{sample.name}</h3>
          <p className="mt-1 text-sm font-medium leading-6 text-slate2">{sample.context}</p>
          <dl className="mt-4 grid gap-1 rounded-[1.5rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4 text-sm" aria-label="Sample results">
            {readingRows(sample).map((row) => (
              <div key={row.label} className="flex flex-wrap justify-between gap-x-3">
                <dt className="font-medium text-ink-soft">{row.label}</dt>
                <dd className="font-extrabold text-ink">{row.value}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs font-medium leading-5 text-slate2">
            Dissolved oxygen saturation at {sample.temperatureC} °C: {formatPercent(saturation)} (context only).
            Conductivity rises with dissolved ions, so it tracks TDS; no guideline value is applied to it here.
          </p>
        </div>

        <div className="min-w-0 space-y-4">
          {hardnessNeeded ? (
            <div className="rounded-[1.5rem] border border-hairline bg-white p-4">
              <label className="block text-sm font-extrabold text-ink">
                Step 1 · Total hardness (mg/L as CaCO₃)
                <input
                  type="number"
                  inputMode="decimal"
                  value={hardnessAnswer}
                  onChange={(event) => {
                    setHardnessAnswer(event.target.value);
                    setHardnessCheck(null);
                  }}
                  className="mt-2 block w-full max-w-xs rounded-2xl border border-hairline bg-white px-4 py-3 text-base font-bold text-ink"
                />
              </label>
              <p className="mt-2 text-xs font-medium leading-5 text-slate2">
                Convert each ion to the mass of CaCO₃ with the same number of moles, then add.
              </p>
              <button
                type="button"
                disabled={hardnessAnswer.trim() === ''}
                onClick={() => setHardnessCheck(checkHardnessAnswer(sample, Number(hardnessAnswer)))}
                className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check hardness
              </button>
              {hardnessCheck ? (
                <div className="mt-3">
                  <AnswerFeedback check={hardnessCheck} working={hardnessWorking(sample)} />
                </div>
              ) : null}
            </div>
          ) : null}

          <fieldset className="rounded-[1.5rem] border border-hairline bg-white p-4" disabled={!hardnessDone}>
            <legend className="px-1 text-sm font-extrabold text-ink">
              {hardnessNeeded ? 'Step 2 · ' : ''}Which results are outside the ADWG guideline?
            </legend>
            {!hardnessDone ? (
              <p className="text-xs font-medium text-slate2">Check your hardness calculation first.</p>
            ) : null}
            <div className="mt-2 grid gap-2">
              {measured.map((parameter) => (
                <label key={parameter} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-hairline px-3 py-2">
                  <input
                    type="checkbox"
                    checked={flags.includes(parameter) && !noneSelected}
                    onChange={() => toggle(parameter)}
                    className="mt-1 accent-brand-coral"
                  />
                  <span className="text-sm">
                    <span className="block font-extrabold text-ink">{QUALITY_GUIDELINES[parameter].label}</span>
                    <span className="block text-xs font-medium text-slate2">
                      {QUALITY_GUIDELINES[parameter].kind} guideline {formatGuideline(parameter)}
                    </span>
                  </span>
                </label>
              ))}
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-hairline px-3 py-2">
                <input
                  type="checkbox"
                  checked={noneSelected}
                  onChange={() => {
                    setNoneSelected((current) => !current);
                    setFlags([]);
                    setResult(null);
                  }}
                  className="mt-1 accent-brand-coral"
                />
                <span className="text-sm font-extrabold text-ink">None — every measured value meets its guideline</span>
              </label>
            </div>
            <button
              type="button"
              disabled={!hardnessDone || (!noneSelected && flags.length === 0)}
              onClick={check}
              className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Compare with guidelines
            </button>
          </fieldset>

          {result ? (
            <div
              role="status"
              aria-label="Guideline comparison"
              className={`rounded-[1.5rem] border p-4 ${result.correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'}`}
            >
              <p className="flex items-center gap-2 font-extrabold text-ink">
                {result.correct ? (
                  <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
                ) : (
                  <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
                )}
                {result.correct ? 'Correct' : 'Not yet'}
              </p>
              <div className="mt-2 space-y-1 text-sm font-medium leading-6 text-ink-soft">
                {result.feedback.map((line) => (
                  <p key={line}>{line}</p>
                ))}
                <p>{sample.explanation}</p>
                <p>
                  Hardness class: {hardnessClass(sampleHardness(sample)) ?? 'unknown'} water.
                </p>
              </div>
              <ul className="mt-3 grid gap-2" aria-label="Guideline meters">
                {measured.map((parameter) => {
                  const value = sampleValue(sample, parameter);
                  return value === undefined ? null : <GuidelineMeter key={parameter} parameter={parameter} value={value} />;
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
